import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, RefreshCw, AlertTriangle, CheckCircle, Clock, Bell, History } from 'lucide-react';
import api from '../api/client';
import { PageHeader, FilterSelect } from '../components/FilterBar';

const TYPES = ['Software','Hardware','AMC','Domain','Certificate','Other'];
const FREQUENCIES = ['monthly','quarterly','biannually','annually','biennial','one-time'];
const CURRENCIES = ['LKR','USD','AED','GBP','EUR','AUD'];

const ALERT_COLORS = {
  overdue:  { bg:'rgba(239,68,68,0.1)', border:'rgba(239,68,68,0.3)', text:'#f87171', label:'OVERDUE' },
  critical: { bg:'rgba(239,68,68,0.07)', border:'rgba(239,68,68,0.2)', text:'#fb923c', label:'Due in ≤7d' },
  urgent:   { bg:'rgba(245,158,11,0.1)', border:'rgba(245,158,11,0.3)', text:'#fbbf24', label:'Due in ≤15d' },
  upcoming: { bg:'rgba(59,130,246,0.08)', border:'rgba(59,130,246,0.2)', text:'#60a5fa', label:'Due in ≤30d' },
  ok:       { bg:'rgba(16,185,129,0.05)', border:'rgba(16,185,129,0.15)', text:'#34d399', label:'OK' },
  unknown:  { bg:'rgba(255,255,255,0.03)', border:'rgba(255,255,255,0.08)', text:'#475569', label:'No date set' },
};

const TYPE_ICONS = {
  Software:'💻', Hardware:'🖥', AMC:'🔧', Domain:'🌐', Certificate:'🔒', Other:'📦'
};

function AlertBadge({ level }) {
  const c = ALERT_COLORS[level] || ALERT_COLORS.unknown;
  return (
    <span style={{ fontSize:10, padding:'2px 7px', borderRadius:4, background:c.bg, border:`1px solid ${c.border}`, color:c.text, fontWeight:600, whiteSpace:'nowrap' }}>
      {c.label}
    </span>
  );
}

// Local calendar date — toISOString() is UTC and would show yesterday in
// Colombo for the first 5½ hours of each day.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Month arithmetic in UTC so a date never slips a day across time zones
const addMonths = (iso, months) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
};
const QUICK_TERMS = [
  { label: '+1 month', months: 1 }, { label: '+3 months', months: 3 },
  { label: '+6 months', months: 6 }, { label: '+1 year', months: 12 },
  { label: '+2 years', months: 24 },
];

// How many seats this renewal covers
const licenceLabel = (r) => r.unlimited_users ? 'Unlimited'
  : (r.user_licenses > 0 ? `${r.user_licenses} user${r.user_licenses === 1 ? '' : 's'}` : null);

export default function ITRenewals() {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  const [alertResult, setAlertResult] = useState(null);
  const [renew, setRenew] = useState(null);     // the Mark Renewed dialog
  const [renewSaving, setRenewSaving] = useState(false);
  const [histModal, setHistModal] = useState(null);
  const [hist, setHist] = useState(null); const [histLoading, setHistLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.renewal_type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await api.get('/renewals', { params });
      setRenewals(r.data);
    } finally { setLoading(false); }
  }, [typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openModal = (r = null) => {
    setForm(r ? { ...r } : { name:'', vendor:'', renewal_type:'Software', category:'', frequency:'annually', cost:'', currency:'LKR', qty:1, user_licenses:'', unlimited_users:false, notes:'', last_renewed:'', next_renewal:'', status:'active' });
    setModal('edit');
    setError('');
    setAlertResult(null);
  };

  const save = async () => {
    if (!form.name || !form.renewal_type) { setError('Name and type required'); return; }
    setSaving(true); setError('');
    try {
      if (form.id) await api.put(`/renewals/${form.id}`, form);
      else await api.post('/renewals', form);
      setModal(null); load();
    } catch (e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  // Opens the dialog with the dates the backend suggests — one year on from
  // today by default — both of which the admin can change before confirming.
  const openRenew = async (r) => {
    setError('');
    try {
      const { data } = await api.get(`/renewals/${r.id}/renew-suggestion`);
      setRenew({ ...data, id: r.id });
    } catch (e) {
      setRenew({
        id: r.id, name: r.name, frequency: r.frequency, cycle_months: null,
        current_next_renewal: r.next_renewal || null,
        renewed_on: todayISO(), next_renewal: addMonths(todayISO(), 12),
      });
    }
  };

  const confirmRenew = async () => {
    if (!renew?.renewed_on || !renew?.next_renewal) { setError('Both dates are required'); return; }
    if (renew.next_renewal <= renew.renewed_on) { setError('The next renewal date must be after the renewed-on date'); return; }
    setRenewSaving(true); setError('');
    try {
      await api.post(`/renewals/${renew.id}/mark-renewed`, {
        renewed_on: renew.renewed_on, next_renewal: renew.next_renewal,
      });
      setRenew(null); load();
    } catch (e) { setError(e.response?.data?.error || 'Failed to mark renewed'); }
    finally { setRenewSaving(false); }
  };

  // What changed on this contract and when — prices, seats and due dates move
  const openHistory = async (r) => {
    setHistModal(r); setHist(null); setHistLoading(true);
    try { const x = await api.get(`/renewals/${r.id}/history`); setHist(x.data); }
    catch (e) { setError(e.response?.data?.error || 'Could not load the history'); }
    finally { setHistLoading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this renewal?')) return;
    await api.delete(`/renewals/${id}`); load();
  };

  const checkAlerts = async () => {
    setCheckingAlerts(true); setAlertResult(null);
    try {
      const r = await api.post('/renewals/check-alerts');
      setAlertResult(r.data);
    } catch (e) { setAlertResult({ error: e.response?.data?.error || 'Failed' }); }
    finally { setCheckingAlerts(false); }
  };

  const overdue = renewals.filter(r => r.alert_level === 'overdue');
  const critical = renewals.filter(r => ['critical','urgent'].includes(r.alert_level));
  const upcoming = renewals.filter(r => r.alert_level === 'upcoming');

  // Annualised spend per currency (monthly ×12, quarterly ×4, biannually ×2, biennial ÷2; one-time excluded)
  const ANNUAL_FACTOR = { monthly: 12, quarterly: 4, biannually: 2, annually: 1, biennial: 0.5 };
  const annualByCurrency = renewals.reduce((acc, r) => {
    if (r.status !== 'active') return acc;
    const f = ANNUAL_FACTOR[r.frequency];
    const c = parseFloat(r.cost) || 0;
    if (!f || !c) return acc;
    const cur = r.currency || 'LKR';
    // `cost` is what is billed each period for the whole subscription, so it
    // is NOT multiplied by the seat count — doing so inflated this figure by
    // the number of users (e.g. M365 Basic 160x).
    acc[cur] = (acc[cur] || 0) + c * f;
    return acc;
  }, {});
  const annualStr = Object.entries(annualByCurrency)
    .sort((a, b) => b[1] - a[1])
    .map(([cur, v]) => `${cur} ${v >= 1000000 ? (v / 1000000).toFixed(2) + 'M' : Math.round(v).toLocaleString()}`)
    .join(' · ') || '—';

  const f = (k) => form[k] ?? '';
  const sf = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const inp = "input-dark";

  const fmtCost = (r) => {
    if (!r.cost || r.cost == 0) return '—';
    return `${r.currency} ${Number(r.cost).toLocaleString()} / ${r.frequency}`;
  };

  return (
    <div className="space-y-4">
      <PageHeader title="IT Renewals">
        <div className="flex gap-2 items-center flex-wrap">
          <button onClick={checkAlerts} disabled={checkingAlerts} className="btn-ghost text-xs" style={{ color:'#fbbf24' }}>
            <Bell size={12}/> {checkingAlerts ? 'Checking...' : 'Run Alert Check'}
          </button>
          <button onClick={() => openModal()} className="btn-primary text-xs"><Plus size={12}/> Add Renewal</button>
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13} className={loading?'animate-spin':''}/></button>
        </div>
      </PageHeader>

      {alertResult && (
        <div className="glass rounded-xl p-3 text-xs" style={{ borderColor: alertResult.error?'rgba(239,68,68,0.3)':'rgba(16,185,129,0.3)', color: alertResult.error?'#f87171':'#34d399' }}>
          {alertResult.error ? `Alert check failed: ${alertResult.error}` :
            alertResult.sent?.length ? `✅ Sent ${alertResult.sent.length} alert email(s): ${alertResult.sent.map(s=>s.name).join(', ')}` :
            `✅ Alert check complete — no new alerts to send (${alertResult.skipped} already notified or OK)`}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <FilterSelect value={statusFilter} onChange={v=>{setStatusFilter(v);}} placeholder="All Statuses" options={[{value:'active',label:'Active'},{value:'inactive',label:'Inactive'},{value:'cancelled',label:'Cancelled'}]}/>
        <FilterSelect value={typeFilter} onChange={v=>{setTypeFilter(v);}} placeholder="All Types" options={TYPES.map(t=>({value:t,label:`${TYPE_ICONS[t]||'📦'} ${t}`}))}/>
      </div>

      {/* Summary dashboard cards */}
      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="glass glow-border rounded-2xl p-4">
            <div className="text-xs font-semibold mb-1" style={{ color:'#475569' }}>Total Renewals</div>
            <div className="text-2xl font-bold" style={{ color:'#60a5fa' }}>{renewals.length}</div>
          </div>
          <div className="glass glow-border rounded-2xl p-4">
            <div className="text-xs font-semibold mb-1" style={{ color:'#475569' }}>Annualised Spend</div>
            <div className="text-lg font-bold" style={{ color:'#34d399', lineHeight:1.3 }}>{annualStr}</div>
            <div className="text-xs mt-0.5" style={{ color:'#334155' }}>
              active renewals, per year — reads Cost as the amount billed each Frequency period
            </div>
          </div>
          <div className="glass glow-border rounded-2xl p-4" style={{ borderColor:overdue.length?'rgba(239,68,68,0.3)':'undefined' }}>
            <div className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color:'#f87171' }}><AlertTriangle size={11}/> Overdue</div>
            <div className="text-2xl font-bold" style={{ color:'#f87171' }}>{overdue.length}</div>
          </div>
          <div className="glass glow-border rounded-2xl p-4">
            <div className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color:'#fbbf24' }}><Clock size={11}/> Due ≤15 days</div>
            <div className="text-2xl font-bold" style={{ color:'#fbbf24' }}>{critical.length}</div>
          </div>
          <div className="glass glow-border rounded-2xl p-4">
            <div className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color:'#60a5fa' }}><Clock size={11}/> Due ≤30 days</div>
            <div className="text-2xl font-bold" style={{ color:'#60a5fa' }}>{upcoming.length}</div>
          </div>
        </div>
      )}

      {/* Alert banners */}
      {!loading && overdue.length > 0 && (
        <div className="rounded-xl p-4" style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)' }}>
          <div className="flex items-center gap-2 mb-2 text-sm font-bold" style={{ color:'#f87171' }}><AlertTriangle size={14}/> {overdue.length} Overdue Renewal{overdue.length>1?'s':''}</div>
          <div className="flex flex-wrap gap-2">
            {overdue.map(r => (
              <span key={r.id} style={{ fontSize:11, background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:6, padding:'3px 8px', color:'#fca5a5' }}>
                {TYPE_ICONS[r.renewal_type]||'📦'} {r.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Renewals table */}
      <div className="glass glow-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table style={{ width:'100%', fontSize:12, borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                {['Renewal','Vendor','Type / Category','Frequency','Licences','Cost','Last Renewed','Next Renewal','Days Left','Alert',''].map(h => (
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:'#334155', fontWeight:700, fontSize:10, textTransform:'uppercase', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} style={{ padding:'32px', textAlign:'center', color:'#334155' }}>
                  <div className="flex justify-center"><div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/></div>
                </td></tr>
              ) : renewals.length === 0 ? (
                <tr><td colSpan={11} style={{ padding:'32px', textAlign:'center', color:'#334155' }}>No renewals found.</td></tr>
              ) : renewals.map(r => {
                const ac = ALERT_COLORS[r.alert_level] || ALERT_COLORS.unknown;
                return (
                  <tr key={r.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', borderLeft:`3px solid ${r.alert_level==='overdue'?'#ef4444':r.alert_level==='critical'?'#f97316':r.alert_level==='urgent'?'#f59e0b':'transparent'}` }}
                    onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <td style={{ padding:'8px 12px' }}>
                      <div style={{ color:'#e2e8f0', fontWeight:500 }}>{TYPE_ICONS[r.renewal_type]||'📦'} {r.name}</div>
                      {r.notes && <div style={{ color:'#334155', fontSize:10, marginTop:2 }}>{r.notes}</div>}
                    </td>
                    <td style={{ padding:'8px 12px', color:'#94a3b8' }}>{r.vendor||'—'}</td>
                    <td style={{ padding:'8px 12px' }}>
                      <div style={{ color:'#60a5fa', fontSize:11, fontWeight:600 }}>{r.renewal_type}</div>
                      {r.category && <div style={{ color:'#475569', fontSize:10 }}>{r.category}</div>}
                    </td>
                    <td style={{ padding:'8px 12px', color:'#94a3b8', textTransform:'capitalize' }}>{r.frequency}</td>
                    <td style={{ padding:'8px 12px', whiteSpace:'nowrap' }}>
                      {licenceLabel(r)
                        ? <span style={{ fontSize:11, fontWeight:600, padding:'2px 7px', borderRadius:4,
                            background: r.unlimited_users ? 'rgba(139,92,246,0.12)' : 'rgba(96,165,250,0.12)',
                            border: `1px solid ${r.unlimited_users ? 'rgba(139,92,246,0.3)' : 'rgba(96,165,250,0.25)'}`,
                            color: r.unlimited_users ? '#a78bfa' : '#60a5fa' }}>{licenceLabel(r)}</span>
                        : <span style={{ color:'#334155' }}>—</span>}
                    </td>
                    <td style={{ padding:'8px 12px', color:'#34d399', fontWeight:600, whiteSpace:'nowrap' }}>{fmtCost(r)}</td>
                    <td style={{ padding:'8px 12px', color:'#475569', whiteSpace:'nowrap' }}>{r.last_renewed||'—'}</td>
                    <td style={{ padding:'8px 12px', color:ac.text, fontWeight:600, whiteSpace:'nowrap' }}>{r.next_renewal||'—'}</td>
                    <td style={{ padding:'8px 12px', textAlign:'center' }}>
                      {r.days_until_renewal != null ? (
                        <span style={{ fontWeight:700, color: r.days_until_renewal<0?'#f87171':r.days_until_renewal<=7?'#fb923c':r.days_until_renewal<=15?'#fbbf24':'#94a3b8' }}>
                          {r.days_until_renewal < 0 ? `${Math.abs(r.days_until_renewal)}d ago` : `${r.days_until_renewal}d`}
                        </span>
                      ) : '—'}
                    </td>
                    <td style={{ padding:'8px 12px' }}><AlertBadge level={r.alert_level}/></td>
                    <td style={{ padding:'8px 12px', whiteSpace:'nowrap' }}>
                      <div style={{ display:'flex', gap:6 }}>
                        {r.status === 'active' && (
                          <button onClick={() => openRenew(r)} title="Mark renewed — asks for the next renewal date"
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#34d399' }}><CheckCircle size={13}/></button>
                        )}
                        <button onClick={() => openHistory(r)} title="Change history — price, seats and due dates"
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8' }}><History size={13}/></button>
                        <button onClick={() => openModal(r)} style={{ background:'none', border:'none', cursor:'pointer', color:'#60a5fa' }}><Edit2 size={13}/></button>
                        <button onClick={() => del(r.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#f87171' }}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alert info note */}
      <div className="text-xs px-1" style={{ color:'#334155' }}>
        Alert emails go to the Head of IT (CC IT dept) at 8:00 AM daily when a renewal is due within 30 / 15 / 7 days, and then every 7 days for as long as it stays overdue — until it is marked renewed. Switch them off in Settings → Emails → Automation Alerts. "Run Alert Check" triggers the same run now.
      </div>

      {/* Edit/Add Modal */}
      {modal === 'edit' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:560 }}>
            <h2 className="text-base font-bold mb-4" style={{ color:'#e2e8f0' }}>{f('id') ? 'Edit' : 'Add'} Renewal</h2>
            {error && <div className="mb-3 p-2 rounded-lg text-xs" style={{ background:'rgba(239,68,68,0.1)', color:'#f87171' }}>{error}</div>}
            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Name *</label>
                <input value={f('name')} onChange={e=>sf('name',e.target.value)} className={inp} placeholder="e.g. M365 Suite - BB"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Vendor</label>
                  <input value={f('vendor')} onChange={e=>sf('vendor',e.target.value)} className={inp} placeholder="e.g. Microsoft"/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Type *</label>
                  <select value={f('renewal_type')} onChange={e=>sf('renewal_type',e.target.value)} className={inp}>
                    {TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t]||'📦'} {t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Category</label>
                  <input value={f('category')} onChange={e=>sf('category',e.target.value)} className={inp} placeholder="e.g. M365, ERP, Firewall"/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Frequency</label>
                  <select value={f('frequency')} onChange={e=>sf('frequency',e.target.value)} className={inp}>
                    {FREQUENCIES.map(fr => <option key={fr} value={fr}>{fr.charAt(0).toUpperCase()+fr.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Cost</label>
                  <input type="number" value={f('cost')} onChange={e=>sf('cost',e.target.value)} className={inp} placeholder="0"/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Currency</label>
                  <select value={f('currency')} onChange={e=>sf('currency',e.target.value)} className={inp}>
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Qty</label>
                  <input type="number" value={f('qty')} onChange={e=>sf('qty',e.target.value)} className={inp} placeholder="1"/>
                </div>
              </div>

              {/* Seat-based contracts — Gensoft ERP, M365, Sophos endpoint */}
              <div className="rounded-lg p-3" style={{ background:'rgba(96,165,250,0.05)', border:'1px solid rgba(96,165,250,0.15)' }}>
                <div className="text-xs font-semibold mb-2" style={{ color:'#60a5fa' }}>User Licences</div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-xs mb-1" style={{ color:'#475569' }}>Number of user licences</label>
                    <input type="number" min="0" value={form.unlimited_users ? '' : f('user_licenses')}
                      disabled={!!form.unlimited_users}
                      onChange={e=>sf('user_licenses', e.target.value)} className={inp}
                      placeholder={form.unlimited_users ? 'Unlimited' : 'e.g. 15'}
                      style={form.unlimited_users ? { opacity:0.45 } : undefined}/>
                  </div>
                  <label className="flex items-center gap-2 text-xs pb-2" style={{ color:'#94a3b8', cursor:'pointer' }}>
                    <input type="checkbox" checked={!!form.unlimited_users}
                      onChange={e=>sf('unlimited_users', e.target.checked)}/>
                    Unlimited / site licence
                  </label>
                </div>
                <div className="text-xs mt-2" style={{ color:'#334155' }}>
                  How many seats the contract covers — e.g. Gensoft ERP is priced per user licence. Leave blank where it does not apply. Cost above stays the amount billed each period, not the price per seat.
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Last Renewed</label>
                  <input type="date" value={f('last_renewed')} onChange={e=>sf('last_renewed',e.target.value)} className={inp}/>
                </div>
                <div>
                  <label className="block text-xs mb-1" style={{ color:'#475569' }}>Next Renewal</label>
                  <input type="date" value={f('next_renewal')} onChange={e=>sf('next_renewal',e.target.value)} className={inp}/>
                </div>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Status</label>
                <select value={f('status')} onChange={e=>sf('status',e.target.value)} className={inp}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Notes</label>
                <input value={f('notes')} onChange={e=>sf('notes',e.target.value)} className={inp} placeholder="User counts, add-ons, contract details..."/>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={save} disabled={saving} className="btn-primary flex-1 text-sm">{saving?'Saving...':'Save'}</button>
              <button onClick={() => setModal(null)} className="btn-ghost flex-1 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Renewed — confirm both dates before anything is written */}
      {renew && (
        <div className="modal-overlay" onClick={() => setRenew(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:480 }}>
            <h2 className="text-base font-bold mb-1" style={{ color:'#e2e8f0' }}>Mark as Renewed</h2>
            <div className="text-xs mb-4" style={{ color:'#94a3b8' }}>
              {renew.name}
              {renew.current_next_renewal && (
                <span style={{ color:'#475569' }}> · was due {renew.current_next_renewal}</span>
              )}
            </div>

            {error && <div className="mb-3 p-2 rounded-lg text-xs" style={{ background:'rgba(239,68,68,0.1)', color:'#f87171' }}>{error}</div>}

            <div className="space-y-3">
              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Renewed on</label>
                <input type="date" value={renew.renewed_on} className={inp}
                  onChange={e => setRenew(p => ({ ...p, renewed_on: e.target.value }))}/>
              </div>

              <div>
                <label className="block text-xs mb-1" style={{ color:'#475569' }}>Next renewal date</label>
                <input type="date" value={renew.next_renewal} className={inp}
                  onChange={e => setRenew(p => ({ ...p, next_renewal: e.target.value }))}/>
                <div className="flex gap-1.5 flex-wrap mt-2">
                  {QUICK_TERMS.map(q => {
                    const target = addMonths(renew.renewed_on || todayISO(), q.months);
                    const on = renew.next_renewal === target;
                    return (
                      <button key={q.label} type="button"
                        onClick={() => setRenew(p => ({ ...p, next_renewal: addMonths(p.renewed_on || todayISO(), q.months) }))}
                        style={{ fontSize:10, fontWeight:600, padding:'3px 8px', borderRadius:5, cursor:'pointer',
                          background: on ? 'rgba(52,211,153,0.15)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${on ? 'rgba(52,211,153,0.4)' : 'rgba(255,255,255,0.08)'}`,
                          color: on ? '#34d399' : '#94a3b8' }}>
                        {q.label}
                      </button>
                    );
                  })}
                </div>
                <div className="text-xs mt-2" style={{ color:'#334155' }}>
                  Pre-filled one year on from the renewed-on date — change it to whatever the contract says.
                  {renew.cycle_months && renew.cycle_months !== 12 && (
                    <> This one is billed <strong style={{ color:'#94a3b8' }}>{renew.frequency}</strong>, so its own cycle would be{' '}
                      <button type="button"
                        onClick={() => setRenew(p => ({ ...p, next_renewal: addMonths(p.renewed_on || todayISO(), p.cycle_months) }))}
                        style={{ background:'none', border:'none', padding:0, cursor:'pointer', color:'#60a5fa', textDecoration:'underline', fontSize:'inherit' }}>
                        {addMonths(renew.renewed_on || todayISO(), renew.cycle_months)}
                      </button>.
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button onClick={confirmRenew} disabled={renewSaving} className="btn-primary flex-1 text-sm">
                {renewSaving ? 'Saving...' : 'Confirm Renewal'}
              </button>
              <button onClick={() => setRenew(null)} className="btn-ghost flex-1 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change history ─────────────────────────────────────────── */}
      {histModal && (
        <div className="modal-overlay" onClick={() => setHistModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth:640 }}>
            <h2 className="text-base font-bold mb-1" style={{ color:'#e2e8f0' }}>Change History</h2>
            <div className="text-xs mb-4" style={{ color:'#94a3b8' }}>{histModal.name}</div>

            {histLoading ? (
              <div className="py-10 text-center text-xs" style={{ color:'#334155' }}>Loading…</div>
            ) : !hist?.history?.length ? (
              <div className="py-8 text-center text-xs" style={{ color:'#475569' }}>
                No changes recorded yet.
                {hist?.added_at && <div className="mt-1" style={{ color:'#334155' }}>
                  Added to SynergyNode on {new Date(hist.added_at).toLocaleDateString('en-GB')}
                </div>}
              </div>
            ) : (
              <div style={{ maxHeight:'58vh', overflowY:'auto' }}>
                {hist.history.map((h, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-2.5 rounded-lg mb-1.5"
                    style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)' }}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold" style={{ color:'#e2e8f0' }}>
                        {hist.labels?.[h.field] || h.field}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color:'#94a3b8' }}>
                        <span style={{ color:'#f87171', textDecoration:'line-through' }}>{h.old_value ?? '—'}</span>
                        <span style={{ color:'#475569' }}> → </span>
                        <span style={{ color:'#34d399', fontWeight:600 }}>{h.new_value ?? '—'}</span>
                      </div>
                      {h.note && <div className="text-xs mt-0.5" style={{ color:'#334155' }}>{h.note}</div>}
                    </div>
                    <div className="text-xs text-right flex-shrink-0" style={{ color:'#475569' }}>
                      {new Date(h.changed_at).toLocaleDateString('en-GB')}
                      <div style={{ color:'#334155' }}>{h.changed_by_name || 'System'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setHistModal(null)} className="btn-primary flex-1 text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
